// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import { InitialRectangle } from 'components/device-initialize/InitialRectangle'
import { AddingMore } from 'components/device-initialize/AddingMore'
import { Form, Row, Col, Input, Select, Button, message } from 'antd'
import { FormInstance } from 'antd/lib/form'
import { addDevices } from 'api/devices'

interface IDevice {
  deviceID: string
  cityID: number
  driverID: number
  taskType: number
  deviceType: number
}

export interface IIndividualUploadProps {
  cities: any[]
  onSuccess: (e) => void
  onCancel: (e: React.MouseEvent<HTMLElement>) => void
}

export interface IIndividualUploadState {
  devices: IDevice[]
  focusedIndex: number
  invalidIDMap: { [deviceID: string]: boolean }
  failedIndices: number[]
  saving: boolean
}

export class IndividualUpload extends React.Component<IIndividualUploadProps, IIndividualUploadState> {
  constructor (props) {
    super(props)
    this.state = {
      devices: [this.createDevice()],
      focusedIndex: 0,
      invalidIDMap: {},
      failedIndices: [],
      saving: false
    }
  }

  private form = React.createRef<FormInstance>()

  componentWillUnmount () {
    this.setState({
      devices: [this.createDevice()],
      focusedIndex: 0,
      invalidIDMap: {},
      failedIndices: [],
      saving: false
    })
  }

  private handleClickRectangle = (index: number) => {
    this.setState(
      {
        focusedIndex: index
      },
      this.changeFormValues
    )
  }

  private changeFormValues () {
    const focusedItem = this.getFocusedItem()
    this.form.current.setFieldsValue({ ...focusedItem })
    if (this.state.invalidIDMap[focusedItem.deviceID]) {
      this.form.current.setFields([
        {
          name: 'deviceID',
          errors: ['This CameraID already exists.']
        }
      ])
    }
  }

  private handleCloseRectangle = (index: number) => {
    const devices = this.state.devices.slice()
    devices.splice(index, 1)
    const failedIndices = this.state.failedIndices.slice()
    failedIndices.splice(index)
    this.state.failedIndices
      .filter(i => i > index)
      .forEach(k => {
        failedIndices.push(k - 1)
      })
    const focusedIndex =
      index === this.state.focusedIndex ? Math.max(0, index - 1) : Math.min(this.state.focusedIndex, devices.length - 1)
    this.setState(
      {
        devices,
        focusedIndex,
        failedIndices
      },
      this.changeFormValues
    )
  }

  private handleAddMore = () => {
    this.form.current.validateFields().then(() => {
      const newDevice = this.createDevice()
      const devices = this.state.devices.concat(newDevice)
      this.setState(
        {
          devices,
          focusedIndex: devices.length - 1
        },
        () => {
          const focusedItem = this.getFocusedItem()
          this.form.current.setFieldsValue({ ...focusedItem })
        }
      )
    })
  }

  private createDevice (): IDevice {
    const { cities } = this.props
    return {
      deviceID: '',
      cityID: cities.length > 0 ? cities[0].id : null,
      driverID: null,
      taskType: 0,
      deviceType: 0
    }
  }

  private isExists = value => {
    const { devices, focusedIndex } = this.state
    return devices.filter((d, i) => i !== focusedIndex).some(d => d.deviceID === value)
  }

  private isDriverExists = (driverID: string): boolean => {
    const { devices, focusedIndex } = this.state
    return devices.filter((d, i) => i !== focusedIndex).some(d => d.driverID && d.driverID.toString() === driverID)
  }

  private handleValuesChange = (changedValues, allValues) => {
    const devices = this.state.devices.slice()
    devices[this.state.focusedIndex] = {
      ...devices[this.state.focusedIndex],
      ...changedValues
    }
    this.setState({
      devices
    })
  }

  private getFocusedItem = () => {
    const { devices, focusedIndex } = this.state
    return devices[focusedIndex]
  }

  private handleFinish = async values => {
    const { onSuccess } = this.props
    const { devices } = this.state
    this.setState({
      saving: true
    })
    try {
      await this.form.current.validateFields()
      const req = this.state.devices.map(d => {
        const data = { ...d }
        for (let k in data) {
          if (data[k] === null) {
            delete data[k]
          }
        }
        return data
      })
      const res = await addDevices(req)
      const result = res.result
      const invalidIDMap = {}
      const failedMap = {}
      let failedDevices = []
      if (result.existDevices.length > 0) {
        result.existDevices.forEach(d => {
          invalidIDMap[d.deviceID] = true
          failedMap[d.deviceID] = true
        })
      }
      if (result.insertFailedDevices.length > 0) {
        result.insertFailedDevices.forEach(d => {
          failedMap[d.deviceID] = true
        })
      }
      if (result.insertSuccessDevices.length > 0) {
        message.success(`${result.insertSuccessDevices.length} cameras successfully added`)
      }
      if (Object.keys(failedMap).length > 0) {
        failedDevices = devices.filter(d => failedMap[d.deviceID])
        this.setState(
          {
            devices: failedDevices,
            invalidIDMap: invalidIDMap,
            focusedIndex: 0,
            failedIndices: failedDevices.map((d, i) => i)
          },
          this.changeFormValues
        )
      }
      if (failedDevices.length === 0) {
        onSuccess(null)
      }
    } catch (error) {
      console.log(error, error.response)
      const invalidIDMap = {}
      const failedMap = {}
      let failedDevices = []
      const result = error.response.data.result
      if (!result) {
        message.error(error.toString())
      }
      if (result.emptyIDCount > 0) {
        message.error('device ID is empty or city ID is empty')
      }
      if (error.response.status === 400) {
        result.existDevices.forEach(d => {
          invalidIDMap[d.deviceID] = true
          failedMap[d.deviceID] = true
        })
      } else if (error.response.status === 500) {
        result.insertFailedDevices.forEach(d => {
          failedMap[d.deviceID] = true
        })
        if (result.insertSuccessDevices.length > 0) {
          message.success(`${result.insertSuccessDevices.length} cameras successfully added`)
        }
      }
      if (Object.keys(failedMap).length > 0) {
        failedDevices = devices.filter(d => failedMap[d.deviceID])
        this.setState(
          {
            devices: failedDevices,
            invalidIDMap: invalidIDMap,
            focusedIndex: 0,
            failedIndices: failedDevices.map((d, i) => i)
          },
          this.changeFormValues
        )
      }
    } finally {
      this.setState({
        saving: false
      })
    }
  }

  render () {
    const { devices, focusedIndex, saving, failedIndices } = this.state
    const { cities, onCancel } = this.props
    const deviceList = devices.map((d, i) => (
      <InitialRectangle
        deviceID={d.deviceID}
        focused={focusedIndex === i}
        closeable={devices.length > 1}
        onClose={this.handleCloseRectangle}
        valid={failedIndices.indexOf(i) === -1}
        onClick={this.handleClickRectangle}
        index={i}
        className='device-list-item'
        key={i}
      />
    ))
    deviceList.push(<AddingMore onClick={this.handleAddMore} className='device-list-item' key='adding-more' />)
    const item = this.getFocusedItem()
    return (
      <div>
        <div className='device-list'>{deviceList}</div>
        <Row justify='space-between' align='middle' className='form-header'>
          <Col>
            <div className='device-id'>{item.deviceID}</div>
          </Col>
          <Col>
            <i className='required-icon'>*</i>are required
          </Col>
        </Row>
        <Form
          className='device-form'
          layout='vertical'
          ref={this.form}
          onValuesChange={this.handleValuesChange}
          initialValues={{ ...devices[0] }}
          onFinish={this.handleFinish}
        >
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name='deviceID'
                label='Camera ID'
                rules={[
                  { required: true },
                  context => ({
                    validator: (rule, value, callback) => {
                      if (this.isExists(value)) {
                        callback('The same cameraID cannot be added at the same time')
                      }
                      callback()
                    }
                  })
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name='cityID' label='City' rules={[{ required: true }]}>
                <Select>
                  {cities.map(city => (
                    <Select.Option key={city.id} value={city.id}>
                      {city.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='driverID'
                label='DAX ID'
                rules={[
                  { required: false },
                  context => ({
                    validator: (rule, value, callback) => {
                      if (value && this.isDriverExists(value.toString())) {
                        callback('The same driverID cannot be added at the same time')
                      }
                      callback()
                    }
                  })
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name='taskType' label='Task Type'>
                <Select>
                  <Select.Option value={0}>Passive</Select.Option>
                  <Select.Option value={1}>Active</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='deviceType' label='Camera Type'>
                <Select>
                  <Select.Option value={0}>2W</Select.Option>
                  <Select.Option value={1}>4W</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row justify='end'>
            <Col>
              <Button onClick={onCancel} style={{ marginRight: 8 }}>
                Dismiss
              </Button>
              <Button type='primary' htmlType='submit' loading={saving}>
                Done
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    )
  }
}
